/* eslint-disable @typescript-eslint/no-explicit-any */
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  SignInState,
  UUID,
  SignInIntroductionResponse,
  SignedInUser,
  TokenPair,
} from './types';
import {
  postSignInIntroduction,
  postSignInIdentification,
  postSignInConfirmation,
} from './thunk';
import { setAuthHintCookie } from '@/utils/auth';

export const initialState: SignInState = {
  loading: false,
  error: null,

  introLoading: false,
  introError: null,

  identificationLoading: false,
  identificationError: null,

  confirmationLoading: false,
  confirmationError: null,

  session_id: null,
  id_types: undefined,
  primary_id_type_id: null,

  user: null,
  token: null,

  message: null,
};

const slice = createSlice({
  name: 'signin',
  initialState,
  reducers: {
    resetSignInState: () => initialState,

    signOut: () => initialState,

    setSessionId: (state, action: PayloadAction<UUID | null>) => {
      state.session_id = action.payload;
    },
    setPrimaryIdType: (state, action: PayloadAction<UUID | null>) => {
      state.primary_id_type_id = action.payload;
    },
    setMessage: (state, action: PayloadAction<string | null>) => {
      state.message = action.payload;
    },

    setTokenFromStorage: (state, action: PayloadAction<TokenPair | null>) => {
      state.token = action.payload;
    },
    setUser: (state, action: PayloadAction<SignedInUser | null>) => {
      state.user = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(postSignInIntroduction.pending, (state) => {
      state.introLoading = true;
      state.introError = null;
      state.message = null;
    });
    builder.addCase(
      postSignInIntroduction.fulfilled,
      (state, action: PayloadAction<SignInIntroductionResponse>) => {
        state.introLoading = false;
        state.message = action.payload?.message ?? 'Introduction successful';

        if (action.payload.session_id)
          state.session_id = action.payload.session_id;

        if (action.payload.id_types?.length) {
          state.id_types = action.payload.id_types;
          const primary =
            action.payload.id_types.find((t) => t.is_primary) ||
            action.payload.id_types[0];
          state.primary_id_type_id = primary?.id ?? null;
        } else if (action.payload.id && action.payload.name) {
          state.id_types = [
            {
              id: action.payload.id,
              name: action.payload.name,
              country: action.payload.country,
              is_required: action.payload.is_required,
              is_primary: action.payload.is_primary,
            },
          ];
          state.primary_id_type_id = action.payload.id ?? null;
        }
      }
    );
    builder.addCase(postSignInIntroduction.rejected, (state, action) => {
      state.introLoading = false;
      state.introError =
        (action.payload as string | undefined) ||
        (action.error?.message ?? 'Failed to start sign in');
      state.message = state.introError;
    });

    builder.addCase(postSignInIdentification.pending, (state) => {
      state.identificationLoading = true;
      state.identificationError = null;
      state.message = null;
    });
    builder.addCase(postSignInIdentification.fulfilled, (state, action) => {
      state.identificationLoading = false;
      state.message = action.payload?.message ?? 'Identification accepted';
      if (action.payload.session_id)
        state.session_id = action.payload.session_id;
    });
    builder.addCase(postSignInIdentification.rejected, (state, action) => {
      state.identificationLoading = false;
      state.identificationError =
        (action.payload as string | undefined) ||
        (action.error?.message ?? 'Failed to submit identification');
      state.message = state.identificationError;
    });

    builder.addCase(postSignInConfirmation.pending, (state) => {
      state.confirmationLoading = true;
      state.confirmationError = null;
      state.message = null;
    });

    builder.addCase(postSignInConfirmation.fulfilled, (state, action) => {
      state.confirmationLoading = false;

      if (typeof window !== 'undefined') {
        localStorage.setItem('accessToken', action.payload.token.access);
        localStorage.setItem('refreshToken', action.payload.token.refresh);
        const displayName =
          action.payload.user.first_name ||
          action.payload.user.username ||
          'User';
        localStorage.setItem('username', displayName);
        localStorage.setItem(
          'userProfile',
          JSON.stringify(action.payload.user)
        );
        setAuthHintCookie(true);

        try {
          localStorage.setItem(
            'auth:event',
            JSON.stringify({ type: 'login', ts: Date.now() })
          );
          localStorage.removeItem('auth:event');
        } catch {}
      }

      state.user = action.payload.user as SignedInUser;
      state.token = action.payload.token as TokenPair;
      state.message = action.payload?.message ?? 'Sign in successful';
    });

    builder.addCase(postSignInConfirmation.rejected, (state, action) => {
      state.confirmationLoading = false;
      state.confirmationError =
        (action.payload as string | undefined) ||
        (action.error?.message ?? 'Failed to confirm sign in');
      state.message = state.confirmationError;
    });
  },
});

export const {
  resetSignInState,
  signOut,
  setSessionId,
  setPrimaryIdType,
  setMessage,
  setTokenFromStorage,
  setUser,
} = slice.actions;

export const signInReducer = slice.reducer;

export const selectSignIn = (root: any): SignInState =>
  (root?.auth?.signin as SignInState) ?? initialState;

export const selectSignInLoading = (root: any) => {
  const s = selectSignIn(root);
  return s.introLoading || s.identificationLoading || s.confirmationLoading;
};

export const selectSignInSessionId = (root: any) =>
  selectSignIn(root).session_id;
export const selectSignInPrimaryIdTypeId = (root: any) =>
  selectSignIn(root).primary_id_type_id;
export const selectSignedInUser = (root: any) => selectSignIn(root).user;
export const selectSignInToken = (root: any) =>
  selectSignIn(root).token ?? null;
export const selectIsLoggedIn = (root: any) =>
  Boolean(selectSignIn(root).token?.access);
export const selectSignInMessage = (root: any) =>
  selectSignIn(root).message ?? null;
export const selectSignInError = (root: any) => {
  const s = selectSignIn(root);
  return (
    s.error ??
    s.introError ??
    s.identificationError ??
    s.confirmationError ??
    null
  );
};
